'use client';

import type { DataSource, LivePriceData } from '../types';
import { apidata } from '../../out/prices';

/**
 * Default state for live price data
 */
export const defaultLivePriceData: LivePriceData = {
  price: null,
  percentChange: null,
  absoluteChange: null,
  color: undefined,
  lastUpdated: undefined,
  isConnected: false,
};

/**
 * Effective source type including US stocks
 */
export type EffectiveSource = DataSource | 'us';

/**
 * Parse WebSocket protobuf message to LivePriceData
 */
export function parseWebSocketMessage(
  message: unknown,
  indKey?: string,
): LivePriceData | null {
  const data = message as apidata.prices.PriceData;

  // If indKey is provided, filter by it
  if (indKey && data?.tick?.entityId !== indKey) {
    return null;
  }
  if (!data?.tick) return null;

  return {
    price:
      typeof data.tick.price === 'number'
        ? Number(data.tick.price.toFixed(2))
        : null,
    percentChange:
      typeof data.tick.percentageChange === 'number'
        ? Number(data.tick.percentageChange.toFixed(2))
        : null,
    absoluteChange:
      typeof data.tick.absoluteChange === 'number'
        ? Number(data.tick.absoluteChange.toFixed(2))
        : null,
    color: data.tick.livePriceColor as string | undefined,
    lastUpdated: data.tick.receivedTs as string | undefined,
    isConnected: true,
  };
}

/**
 * Parse NATS message to LivePriceData
 */
export function parseNatsMessage(data: unknown): LivePriceData {
  const message = data as Record<string, unknown>;

  return {
    price:
      typeof message?.live_price === 'number'
        ? Number(message.live_price.toFixed(2))
        : null,
    percentChange:
      typeof message?.perc_change_float === 'number'
        ? Number(message.perc_change_float.toFixed(2))
        : null,
    absoluteChange:
      typeof message?.abs_change === 'number'
        ? Number(message.abs_change.toFixed(2))
        : null,
    color: message?.live_price_color as string | undefined,
    lastUpdated: message?.last_updated as string | undefined,
    isConnected: true,
  };
}

// ============================================
// Shared Types
// ============================================

/**
 * Base options shared by all live price hooks
 */
export interface BaseLivePriceOptions {
  /** The indKey to filter messages (optional) */
  indKey?: string;
  /** Whether the subscription is enabled */
  enabled?: boolean;
}

/**
 * Options for Indian stocks (WebSocket + NATS)
 */
export interface IndianStocksOptions extends BaseLivePriceOptions {
  /** WebSocket path for Indian stocks (e.g., 'INDI00012:NSE:I') */
  webSocketPath?: string;
  /** NATS subject path (e.g., 'prices.NSE.RELIANCE') */
  natsSocketPath?: string;
  /** Preferred price source. Defaults to 'websocket'. Falls back to 'nats' if webSocketPath is missing */
  source?: DataSource;
  /** US stocks path - not allowed with Indian stocks options */
  usWebSocketPath?: never;
}

/**
 * Options for US stocks
 */
export interface USStocksOptions extends BaseLivePriceOptions {
  /** WebSocket path for US stocks (e.g., 'AAPL:NASDAQ:US') */
  usWebSocketPath: string;
  /** Indian WebSocket path - not allowed with US stocks options */
  webSocketPath?: never;
  /** NATS path - not allowed with US stocks options */
  natsSocketPath?: never;
  /** Source - not applicable for US stocks */
  source?: never;
}

/**
 * Options for Indian stocks multiple subscriptions
 */
export interface IndianStocksMultipleOptions {
  /** Array of WebSocket paths (optional) */
  webSocketPaths?: string[];
  /** Array of NATS subject paths */
  natsSocketPaths?: string[];
  /** Preferred price source */
  source?: DataSource;
  /** US stocks paths - not allowed with Indian stocks */
  usWebSocketPaths?: never;
  /** Whether the subscription is enabled */
  enabled?: boolean;
}

/**
 * Options for US stocks multiple subscriptions
 */
export interface USStocksMultipleOptions {
  /** Array of US stocks WebSocket paths */
  usWebSocketPaths: string[];
  /** Indian WebSocket paths - not allowed with US stocks */
  webSocketPaths?: never;
  /** NATS paths - not allowed with US stocks */
  natsSocketPaths?: never;
  /** Source - not applicable for US stocks */
  source?: never;
  /** Whether the subscription is enabled */
  enabled?: boolean;
}

/**
 * Check if options are for US stocks
 */
export function isUSStocksOptions(
  options: IndianStocksOptions | USStocksOptions,
): options is USStocksOptions {
  return 'usWebSocketPath' in options && !!options.usWebSocketPath;
}

/**
 * Check if multiple options are for US stocks
 */
export function isUSStocksMultipleOptions(
  options: IndianStocksMultipleOptions | USStocksMultipleOptions,
): options is USStocksMultipleOptions {
  return (
    'usWebSocketPaths' in options &&
    !!options.usWebSocketPaths &&
    options.usWebSocketPaths.length > 0
  );
}

/**
 * Compute effective source for single subscription
 */
export function getEffectiveSource(
  isUSStocks: boolean,
  webSocketPath?: string,
  natsSocketPath?: string,
  source: DataSource = 'websocket',
): EffectiveSource {
  if (isUSStocks) return 'us';
  if (!webSocketPath && natsSocketPath) return 'nats';
  return source;
}

/**
 * Compute effective source for multiple subscriptions
 */
export function getEffectiveSourceMultiple(
  isUSStocks: boolean,
  webSocketPaths?: string[],
  natsSocketPaths?: string[],
  source: DataSource = 'websocket',
): EffectiveSource {
  if (isUSStocks) return 'us';
  if (
    (!webSocketPaths || webSocketPaths.length === 0) &&
    natsSocketPaths &&
    natsSocketPaths.length > 0
  ) {
    return 'nats';
  }
  return source;
}
